import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Users, Building2, Package, Upload, Download, Edit, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "../hooks/use-toast";
import { queryClient, apiRequest } from "../lib/queryClient";
import type { Transporter, Client, Supplier } from "../../../shared/schema";
import { insertTransporterSchema, insertClientSchema, insertSupplierSchema } from "../../../shared/schema";

// Form schemas
const transporterSchema = insertTransporterSchema.extend({
  email: z.string().email().optional().or(z.literal("")),
  status: z.enum(["active", "inactive"])
});

const clientSchema = insertClientSchema.extend({
  email: z.string().email().optional().or(z.literal("")),
  status: z.enum(["active", "inactive"])
});

const supplierSchema = insertSupplierSchema.extend({
  email: z.string().email().optional().or(z.literal("")),
  status: z.enum(["active", "inactive"])
});

const createFormSchema = z.object({
  entityType: z.enum(["clients", "suppliers", "transporters"]),
  name: z.string().min(1, "Company name is required"),
  contactPerson: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().optional(),
  address: z.string().optional(),
  registrationNumber: z.string().optional(),
  status: z.enum(["active", "inactive"])
});

type TransporterFormData = z.infer<typeof transporterSchema>;
type ClientFormData = z.infer<typeof clientSchema>;
type SupplierFormData = z.infer<typeof supplierSchema>;
type CreateFormData = z.infer<typeof createFormSchema>;

export default function BusinessPartners() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("clients");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setIsCreateDialogOpen(false);
    setIsUploadDialogOpen(false);
    setIsEditDialogOpen(false);
    setSelectedEntity(null);
  };

  const handleCreateDialogChange = (open: boolean) => {
    setIsCreateDialogOpen(open);
    if (open) {
      // Reset form with current active tab as default entity type
      createForm.reset({
        entityType: activeTab as "clients" | "suppliers" | "transporters",
        name: "",
        contactPerson: "",
        email: "",
        phone: "",
        address: "",
        registrationNumber: "",
        status: "active",
      });
    }
  };

  // Data queries
  const { data: transporters = [], isLoading: isLoadingTransporters } = useQuery({
    queryKey: ["/api/transporters"],
    enabled: activeTab === "transporters",
  });

  const { data: clients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ["/api/clients"],
    enabled: activeTab === "clients",
  });

  const { data: suppliers = [], isLoading: isLoadingSuppliers } = useQuery({
    queryKey: ["/api/suppliers"],
    enabled: activeTab === "suppliers",
  });

  // Generic mutations
  const createMutation = useMutation({
    mutationFn: (data: { endpoint: string; payload: any; entityType: string }) => 
      apiRequest("POST", data.endpoint, data.payload),
    onSuccess: (_, variables) => {
      // Invalidate queries for the created entity type
      queryClient.invalidateQueries({ queryKey: [`/api/${variables.entityType}`] });
      setIsCreateDialogOpen(false);
      createForm.reset({
        entityType: activeTab as "clients" | "suppliers" | "transporters",
        name: "",
        contactPerson: "",
        email: "",
        phone: "",
        address: "",
        registrationNumber: "",
        status: "active",
      });
      toast({
        title: "Success",
        description: `${variables.entityType.slice(0, -1)} created successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create entity",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { endpoint: string; payload: any }) => 
      apiRequest("PUT", data.endpoint, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/${activeTab}`] });
      setIsEditDialogOpen(false);
      setSelectedEntity(null);
      toast({
        title: "Success",
        description: `${activeTab.slice(0, -1)} updated successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || `Failed to update ${activeTab.slice(0, -1)}`,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (endpoint: string) => apiRequest("DELETE", endpoint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/${activeTab}`] });
      toast({
        title: "Success",
        description: `${activeTab.slice(0, -1)} deleted successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || `Failed to delete ${activeTab.slice(0, -1)}`,
        variant: "destructive",
      });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('excelFile', file);
      return fetch(`/api/${activeTab}/upload`, {
        method: 'POST',
        body: formData,
      }).then(res => {
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/${activeTab}`] });
      setIsUploadDialogOpen(false);
      setUploadFile(null);
      toast({
        title: "Success",
        description: data.message || `Successfully imported ${data.data?.length || 0} ${activeTab}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to upload file",
        variant: "destructive",
      });
    },
  });

  // Forms
  const createForm = useForm<CreateFormData>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      entityType: activeTab as "clients" | "suppliers" | "transporters",
      name: "",
      contactPerson: "",
      email: "",
      phone: "",
      address: "",
      registrationNumber: "",
      status: "active",
    },
  });

  const editForm = useForm({
    resolver: zodResolver(activeTab === "transporters" ? transporterSchema : activeTab === "clients" ? clientSchema : supplierSchema),
  });

  // Helper functions
  const getCurrentData = (): any[] => {
    switch (activeTab) {
      case "transporters": return transporters as any[];
      case "clients": return clients as any[];
      case "suppliers": return suppliers as any[];
      default: return [];
    }
  };

  const getCurrentLoading = (): boolean => {
    switch (activeTab) {
      case "transporters": return isLoadingTransporters;
      case "clients": return isLoadingClients;
      case "suppliers": return isLoadingSuppliers;
      default: return false;
    }
  };

  const handleCreate = (data: CreateFormData) => {
    const { entityType, ...payload } = data;
    createMutation.mutate({
      endpoint: `/api/${entityType}`,
      payload,
      entityType,
    });
  };

  const handleEdit = (entity: any) => {
    setSelectedEntity(entity);
    editForm.reset({
      name: entity.name,
      contactPerson: entity.contactPerson || "",
      email: entity.email || "",
      phone: entity.phone || "",
      address: entity.address || "",
      registrationNumber: entity.registrationNumber || "",
      status: entity.status,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = (data: any) => {
    if (selectedEntity) {
      updateMutation.mutate({
        endpoint: `/api/${activeTab}/${selectedEntity.id}`,
        payload: data,
      });
    }
  };

  const handleDelete = (id: number) => {
    if (confirm(`Are you sure you want to delete this ${activeTab.slice(0, -1)}?`)) {
      deleteMutation.mutate(`/api/${activeTab}/${id}`);
    }
  };

  const handleFileUpload = () => {
    if (uploadFile) {
      uploadMutation.mutate(uploadFile);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `/api/${activeTab}/export/excel`;
    link.download = `${activeTab}-export-${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getEntityIcon = () => {
    switch (activeTab) {
      case "transporters": return Users;
      case "clients": return Building2;
      case "suppliers": return Package;
      default: return Building2;
    }
  };

  const currentData = getCurrentData();
  const currentLoading = getCurrentLoading();
  const EntityIcon = getEntityIcon();

  return (
    <div>
      <div className="space-y-6 p-6">
        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="flex justify-between items-center mb-6">
            <TabsList className="grid grid-cols-3 w-fit">
              <TabsTrigger value="clients" className="flex items-center gap-2" data-testid="tab-clients">
                <Building2 className="h-4 w-4" />
                <span>Clients</span>
              </TabsTrigger>
              <TabsTrigger value="suppliers" className="flex items-center gap-2" data-testid="tab-suppliers">
                <Package className="h-4 w-4" />
                <span>Suppliers</span>
              </TabsTrigger>
              <TabsTrigger value="transporters" className="flex items-center gap-2" data-testid="tab-transporters">
                <Users className="h-4 w-4" />
                <span>Transporters</span>
              </TabsTrigger>
            </TabsList>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownload} data-testid="button-download">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="button-upload">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload {activeTab} Data</DialogTitle>
                    <DialogDescription>
                      Upload an Excel file to import {activeTab} data.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      data-testid="input-file-upload"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={handleFileUpload}
                      disabled={!uploadFile || uploadMutation.isPending}
                      data-testid="button-submit-upload"
                    >
                      {uploadMutation.isPending ? "Uploading..." : "Upload"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={isCreateDialogOpen} onOpenChange={handleCreateDialogChange}>
                <DialogTrigger asChild>
                  <Button data-testid="button-add-new">
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New Business Partner</DialogTitle>
                    <DialogDescription>
                      Add a new client, supplier, or transporter to the system.
                    </DialogDescription>
                  </DialogHeader>
                  <Form {...createForm}>
                    <form onSubmit={createForm.handleSubmit(handleCreate)} className="space-y-4">
                      <FormField
                        control={createForm.control}
                        name="entityType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Entity Type</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-entity-type">
                                  <SelectValue placeholder="Select entity type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="clients">Client</SelectItem>
                                <SelectItem value="suppliers">Supplier</SelectItem>
                                <SelectItem value="transporters">Transporter</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Company name" {...field} data-testid="input-name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createForm.control}
                        name="contactPerson"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact Person</FormLabel>
                            <FormControl>
                              <Input placeholder="John Doe" {...field} data-testid="input-contact-person" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input placeholder="contact@company.com" {...field} data-testid="input-email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createForm.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                              <Input placeholder="+1234567890" {...field} data-testid="input-phone" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createForm.control}
                        name="address"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Address</FormLabel>
                            <FormControl>
                              <Textarea placeholder="Company address" {...field} data-testid="input-address" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createForm.control}
                        name="registrationNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Registration Number</FormLabel>
                            <FormControl>
                              <Input placeholder="REG123456" {...field} data-testid="input-registration" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={createForm.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Status</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-status">
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="inactive">Inactive</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <DialogFooter>
                        <Button
                          type="submit"
                          disabled={createMutation.isPending}
                          data-testid="button-submit-create"
                        >
                          {createMutation.isPending ? "Creating..." : "Create"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Tab Content */}
          <TabsContent value={activeTab} className="mt-0">
            {activeTab === "transporters" ? (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <EntityIcon className="h-5 w-5" />
                          Transporter Companies
                        </CardTitle>
                        <CardDescription>
                          {currentData.length} transporter companies registered. Click on a company to view details, trucks, trailers, and drivers.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {currentLoading ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <div className="animate-pulse">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                        </div>
                      </div>
                    ) : currentData.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <EntityIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No transporters found</p>
                        <p className="text-sm">Click "Add New" to get started</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {currentData.map((entity: any) => (
                          <div
                            key={entity.id}
                            className="p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                            data-testid={`card-transporter-${entity.id}`}
                            onClick={() => setLocation(`/transporters/${entity.id}`)}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-medium" data-testid={`text-transporter-name-${entity.id}`}>
                                {entity.name}
                              </h3>
                              <Badge variant={entity.status === "active" ? "default" : "secondary"}>
                                {entity.status}
                              </Badge>
                            </div>
                            
                            {entity.contactPerson && (
                              <p className="text-sm text-muted-foreground mb-1">
                                Contact: {entity.contactPerson}
                              </p>
                            )}
                            
                            {entity.email && (
                              <p className="text-sm text-muted-foreground mb-1">
                                {entity.email}
                                  </p>
                                )}
                                
                                {entity.phone && (
                                  <p className="text-sm text-muted-foreground mb-3">
                                    {entity.phone}
                                  </p>
                                )}
                                
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(entity);
                                    }}
                                    data-testid={`button-edit-${entity.id}`}
                                  >
                                    <Edit className="w-4 h-4 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(entity.id);
                                    }}
                                    data-testid={`button-delete-${entity.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <EntityIcon className="h-5 w-5" />
                        {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                      </CardTitle>
                      <CardDescription>
                        {currentData.length} {activeTab} registered
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {currentLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                      </div>
                    </div>
                  ) : currentData.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <EntityIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No {activeTab} found</p>
                      <p className="text-sm">Click "Add New" to get started</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {currentData.map((entity: any) => (
                        <div
                          key={entity.id}
                          className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                          data-testid={`card-${activeTab.slice(0, -1)}-${entity.id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium" data-testid={`text-${activeTab.slice(0, -1)}-name-${entity.id}`}>
                              {entity.name}
                            </h3>
                            <Badge variant={entity.status === "active" ? "default" : "secondary"}>
                              {entity.status}
                            </Badge>
                          </div>
                          
                          {entity.contactPerson && (
                            <p className="text-sm text-muted-foreground mb-1">
                              Contact: {entity.contactPerson}
                            </p>
                          )}
                          
                          {entity.email && (
                            <p className="text-sm text-muted-foreground mb-1">
                              {entity.email}
                            </p>
                          )}
                          
                          {entity.phone && (
                            <p className="text-sm text-muted-foreground mb-3">
                              {entity.phone}
                            </p>
                          )}
                          
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(entity)}
                              data-testid={`button-edit-${entity.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(entity.id)}
                              data-testid={`button-delete-${entity.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit {activeTab.slice(0, -1)}</DialogTitle>
              <DialogDescription>
                Update {activeTab.slice(0, -1)} information.
              </DialogDescription>
            </DialogHeader>
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(handleUpdate)} className="space-y-4">
                <FormField
                  control={editForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Company name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="contactPerson"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Person</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="contact@company.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="+1234567890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Company address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="registrationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Registration Number</FormLabel>
                      <FormControl>
                        <Input placeholder="REG123456" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? "Updating..." : `Update ${activeTab.slice(0, -1)}`}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}